import React from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Box,
  Button,
  Group,
  GroupSeparator,
  GroupText,
  Input,
  Text,
  randomPickerColor,
  type Color,
} from "@salestrak/ui";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { useAuthEmail } from "#app/lib/hooks/use-auth-email";
import { useSplashAutoFocus } from "#app/lib/splash-context";
import { WorkspaceScreenLayout } from "#app/components/workspace-screen-layout";
import {
  normalizeIdentifierInput,
  toUrlFriendly,
  useWorkspaceCreate,
} from "#app/lib/hooks/use-workspace-create";
import { routePath } from "#app/lib/hooks/use-app-navigate";
import { AnalyticsContext, WorkspaceEvent, trackEvent } from "#app/lib/analytics";

const NAME_MAX_LENGTH = 80;

function WorkspaceCreateScreen() {
  const navigate = useNavigate();

  const [name, setName] = React.useState("");
  const [identifier, setIdentifier] = React.useState("");
  // Auto-derive the identifier from the name until the user edits it by hand;
  // clearing the field hands derivation back to the name.
  const [identifierDirty, setIdentifierDirty] = React.useState(false);
  // The avatar color is seeded at random (greys excluded) so a fresh workspace
  // never lands on the "unset" grey; it is re-colored later in settings.
  const [avatarColor] = React.useState<Color>(() => randomPickerColor());

  const { urlPath, urlPathError, isCreating, createWorkspace, enterWorkspace } =
    useWorkspaceCreate(identifier);

  const userEmail = useAuthEmail();
  const nameRef = useSplashAutoFocus();

  const handleNameChange = React.useCallback(
    (value: string) => {
      setName(value);
      if (!identifierDirty) setIdentifier(toUrlFriendly(value));
    },
    [identifierDirty],
  );

  const handleIdentifierChange = React.useCallback((value: string) => {
    const normalized = normalizeIdentifierInput(value);
    setIdentifier(normalized);
    setIdentifierDirty(normalized.length > 0);
  }, []);

  // Errors surface on the fields instead of disabling the CTA: required-field
  // messages appear once a submit is attempted; the length error is live.
  const [submitAttempted, setSubmitAttempted] = React.useState(false);

  const trimmedName = name.trim();
  const nameError =
    trimmedName.length > NAME_MAX_LENGTH
      ? `Name must be at most ${NAME_MAX_LENGTH} characters`
      : submitAttempted && trimmedName.length === 0
        ? "Required"
        : null;
  const urlError =
    urlPathError ??
    (submitAttempted && urlPath.length === 0 ? "Required" : null);

  const isValid =
    trimmedName.length > 0 &&
    trimmedName.length <= NAME_MAX_LENGTH &&
    urlPath.length > 0 &&
    !urlPathError;

  const handleCreate = React.useCallback(async () => {
    if (isCreating) return;
    if (!isValid) {
      setSubmitAttempted(true);
      return;
    }
    try {
      const workspace = await createWorkspace(name, identifier, avatarColor);
      await enterWorkspace(workspace);
      // After the switch, so the event belongs to the new workspace.
      trackEvent({
        context: AnalyticsContext.Workspace,
        event: WorkspaceEvent.Create,
      });
      navigate({ to: routePath(`/${workspace.urlPath}`), replace: true });
    } catch (err: any) {
      console.error("[WorkspaceCreate] Failed to create workspace", err);
      toastMutationError(err, "Failed to create Workspace");
    }
  }, [
    isValid,
    isCreating,
    name,
    identifier,
    avatarColor,
    createWorkspace,
    enterWorkspace,
    navigate,
  ]);

  // A real form, so Enter in either field submits.
  const handleSubmit = React.useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      void handleCreate();
    },
    [handleCreate],
  );

  return (
    <WorkspaceScreenLayout
      email={userEmail}
      onBack={() => navigate({ to: "/workspace-select", replace: true })}
    >
      {/* Box carries no margin props — block separation is bottom padding.
          Rhythm: 12 heading→subtext, 32 subtext→form, 12 label→input,
          24 between fields, 48 fields→CTA (form gap 24 + 24 pad). */}
      <Box align="center">
        <Box as="form" onSubmit={handleSubmit} fill="width" maxWidth={400}>
          <Box paddingBottom="base">
            <Text as="h3" size="xl" weight="medium" align="center">
              Create a Workspace
            </Text>
          </Box>

          <Box paddingBottom="xl">
            {/* as="p": an inline span is fit-content, so text-center is a no-op. */}
            <Text as="p" size="base" weight="medium" variant="muted" align="center">
              Manage projects, contacts and teams in one place
            </Text>
          </Box>

          <Box gap="lg">
            <Box gap="base">
              <Text
                as="label"
                htmlFor="ws-name"
                size="sm"
                weight="medium"
                variant="faint"
              >
                Name
              </Text>
              {/* Inner Box: label→control keeps the 12px rhythm while the
                  error sits a tighter 8px under its field. */}
              <Box gap="sm">
                <Input
                  id="ws-name"
                  size="xl"
                  value={name}
                  onChange={(event) => handleNameChange(event.target.value)}
                  aria-invalid={!!nameError}
                  ref={nameRef}
                />
                {nameError ? (
                  <Text size="xs" weight="medium" variant="destructive">
                    {nameError}
                  </Text>
                ) : null}
              </Box>
            </Box>

            <Box gap="base">
              <Text
                as="label"
                htmlFor="ws-url"
                size="sm"
                weight="medium"
                variant="faint"
              >
                URL
              </Text>
              <Box gap="sm">
                <Group fullWidth>
                  <GroupText>salestrak.io/</GroupText>
                  <GroupSeparator />
                  <Input
                    id="ws-url"
                    size="xl"
                    value={identifier}
                    onChange={(event) =>
                      handleIdentifierChange(event.target.value)
                    }
                    autoCapitalize="none"
                    aria-invalid={!!urlError}
                  />
                </Group>
                {urlError ? (
                  <Text size="xs" weight="medium" variant="destructive">
                    {urlError}
                  </Text>
                ) : null}
              </Box>
            </Box>

            {/* direction opts into flex — a bare Box is display:block and the
                inline-flex Button would hug its label instead of spanning. */}
            <Box direction="column" paddingTop="lg">
              <Button
                type="submit"
                color="neutral"
                ink="foreground"
                size="xl"
                loading={isCreating}
              >
                Create Workspace
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </WorkspaceScreenLayout>
  );
}

// The lobby: signed in, but not yet in a space whose look applies.
export const Route = createFileRoute("/(app)/workspace-create")({
  component: WorkspaceCreateScreen,
  staticData: { standardAppearance: true },
});
